import { useSeo } from '../hooks/useSeo';

export function PrivacyPolicy() {
  useSeo('Privacy Policy — Media Magic');
  return (
    <div className="portal-surface" style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <h1 style={{ fontSize: 32, marginBottom: 8, color: 'var(--ink-950)' }}>Privacy Policy</h1>
      <p style={{ color: 'var(--ink-500)', marginBottom: 32, fontSize: 14 }}>
        Last updated: March 2026 | Effective Date: March 2026
      </p>

      <section style={styles.section}>
        <h2 style={styles.h2}>1. Introduction & Scope</h2>
        <p style={styles.p}>
          Media Magic ("Company," "we," "our," or "us") is committed to protecting your privacy and ensuring transparency in how we handle personal information. This Privacy Policy explains how we collect, use, disclose, retain, and safeguard your information when you use our website, services, and applications (collectively, the "Service"). Please read this policy carefully. If you do not agree with our practices, please do not use our Service.
        </p>
        <p style={styles.p}>
          This policy applies to all users, including personal users and developers using our SDK or API. We comply with applicable privacy laws including GDPR (EU), CCPA (California), and other regional privacy requirements.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>2. Information We Collect</h2>

        <h3 style={styles.h3}>2.1 Account Information</h3>
        <p style={styles.p}>
          When you create an account, we collect: email address, username, authentication credentials (password hash), and any profile information you voluntarily provide (name, company, etc.). We do not store plain-text passwords; authentication is managed through AWS Cognito.
        </p>

        <h3 style={styles.h3}>2.2 Image Data & Content</h3>
        <p style={styles.p}>
          <strong>Uploaded Images:</strong> Images you upload are processed to create optimized versions (WebP, AVIF, etc.). Original images are temporarily stored in S3 for processing, then deleted after optimization. Optimized images are cached on our CloudFront CDN for up to 7 days for performance purposes.
        </p>
        <p style={styles.p}>
          <strong>Try Now Feature:</strong> If you use the "Try Now" feature to submit a website URL for demonstration, we crawl the specified domain using Playwright, extract images, transform them, and generate a temporary preview. These crawl results are stored in DynamoDB with automatic deletion after 7 days. We do not store the original website content beyond the images extracted.
        </p>
        <p style={styles.p}>
          <strong>Image Metadata:</strong> We collect and retain metrics about processed images including: original and optimized dimensions, file sizes, format conversions applied, transformation parameters (quality, crops, autofocus mode), cache status, and request IDs for up to 90 days for analytics purposes.
        </p>

        <h3 style={styles.h3}>2.3 Usage & Analytics Data</h3>
        <p style={styles.p}>
          We collect information about your platform interactions including:
        </p>
        <ul style={styles.list}>
          <li>API requests and SDK usage patterns</li>
          <li>Image processing requests and transformations applied</li>
          <li>Optimization metrics and performance statistics</li>
          <li>Device type, browser version, and operating system</li>
          <li>IP address, access timestamps, and session duration</li>
          <li>Referring domain and destination pages</li>
          <li>Error messages and support requests</li>
        </ul>
        <p style={styles.p}>
          Analytics are collected via Google Analytics 4 (GA4) and aggregated CloudWatch logs. We use this data to improve service performance, identify bugs, and understand feature usage.
        </p>

        <h3 style={styles.h3}>2.4 Cookies & Similar Technologies</h3>
        <p style={styles.p}>
          We use cookies and local storage for: session authentication (Cognito session tokens), preference storage (display settings, dashboard state), and analytics tracking (GA4 cookies). By using our Service, you consent to cookie usage. You can disable cookies in your browser settings, though this may affect functionality.
        </p>

        <h3 style={styles.h3}>2.5 Payment & Billing Information</h3>
        <p style={styles.p}>
          If you subscribe to paid services, we collect billing information including name, address, and payment card details. Payment processing is handled by third-party providers (Stripe, AWS Billing) and is not stored directly by Media Magic. We retain billing records as required by law (7 years for tax compliance).
        </p>

        <h3 style={styles.h3}>2.6 Support & Communications</h3>
        <p style={styles.p}>
          When you contact support, we collect your email, message content, and any attachments to respond to your inquiry and improve our service. Support conversations are retained for 90 days unless a longer retention period is required.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>3. Legal Basis for Processing (GDPR)</h2>
        <p style={styles.p}>
          If you are in the EU or UK, we process your personal data based on:
        </p>
        <ul style={styles.list}>
          <li><strong>Contract:</strong> Processing necessary to provide the Service (account creation, image optimization)</li>
          <li><strong>Legitimate Interests:</strong> Fraud prevention, security, analytics, and service improvement</li>
          <li><strong>Legal Obligation:</strong> Compliance with law enforcement requests and tax requirements</li>
          <li><strong>Consent:</strong> Marketing communications and optional analytics (you may withdraw consent anytime)</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>4. How We Use Your Information</h2>
        <p style={styles.p}>We use your information for:</p>
        <ul style={styles.list}>
          <li>Providing and improving image optimization and transformation services</li>
          <li>Authenticating your account and protecting against unauthorized access</li>
          <li>Processing image uploads, transformations, and API requests</li>
          <li>Sending service announcements, security alerts, and billing notifications</li>
          <li>Analyzing usage patterns and optimizing platform performance</li>
          <li>Preventing, detecting, and addressing fraud, abuse, and security incidents</li>
          <li>Complying with legal obligations and responding to legal requests</li>
          <li>Generating usage analytics and reporting (in aggregated form)</li>
          <li>Improving our products through A/B testing and feature analysis</li>
        </ul>
        <p style={styles.p}>
          <strong>We do not:</strong> Sell personal information to third parties, use images for training AI models without explicit consent, or share data for marketing purposes beyond service improvement.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>5. Data Retention & Deletion</h2>
        <p style={styles.p}>
          <strong>Account Data:</strong> Retained while your account is active. Upon account deletion, we permanently delete personal information within 30 days, except where retention is legally required.
        </p>
        <p style={styles.p}>
          <strong>Image Data:</strong> Original uploaded images are deleted after optimization (typically within 24 hours). Optimized images cached on CDN expire after 7 days. Try Now crawl results automatically delete after 7 days.
        </p>
        <p style={styles.p}>
          <strong>Analytics & Logs:</strong> Aggregated usage metrics retained for 90 days. Raw access logs retained for 30 days. CloudWatch logs retained per AWS default (14 days).
        </p>
        <p style={styles.p}>
          <strong>Billing Records:</strong> Retained for 7 years for tax compliance as required by law.
        </p>
        <p style={styles.p}>
          <strong>Support Communications:</strong> Retained for 90 days or longer if needed to resolve ongoing issues.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>6. Data Sharing & Third Parties</h2>
        <p style={styles.p}>
          <strong>We do not sell your personal information.</strong> We may share information with:
        </p>
        <ul style={styles.list}>
          <li><strong>Service Providers:</strong> AWS (hosting, S3, CloudFront, DynamoDB), AWS Cognito (authentication), Google Analytics (analytics), Stripe (payment processing). These providers are bound by data processing agreements and may only use data to provide their services.</li>
          <li><strong>Law Enforcement:</strong> When required by legal process, court order, or government request. We will notify you unless legally prohibited.</li>
          <li><strong>Business Transfers:</strong> If Media Magic is acquired, merged, or assets transferred, your data may be transferred as part of that transaction. We will provide notice of any such change.</li>
          <li><strong>With Your Consent:</strong> We may share data with third parties if you explicitly authorize it.</li>
        </ul>
        <p style={styles.p}>
          <strong>International Transfers:</strong> We store data primarily in the US (US-East-1 AWS region). If you are in the EU/UK, transfer to the US is authorized under Standard Contractual Clauses (SCCs). You have the right to object to transfers; contact us for more information.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>7. Security Measures</h2>
        <p style={styles.p}>
          We implement industry-standard security including:
        </p>
        <ul style={styles.list}>
          <li>End-to-end TLS/SSL encryption for data in transit</li>
          <li>AES-256 encryption for data at rest in S3</li>
          <li>Secure authentication via AWS Cognito with optional MFA</li>
          <li>Regular security audits and penetration testing</li>
          <li>Access controls limiting employee data access</li>
          <li>Automated threat detection and intrusion prevention</li>
        </ul>
        <p style={styles.p}>
          <strong>However, no system is completely secure.</strong> We cannot guarantee absolute security of your data. You are responsible for maintaining the confidentiality of your account credentials.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>8. Your Privacy Rights</h2>
        <p style={styles.p}>
          <strong>For EU/UK residents (GDPR):</strong> You have the right to:
        </p>
        <ul style={styles.list}>
          <li><strong>Access:</strong> Request a copy of your personal data</li>
          <li><strong>Correction:</strong> Request we correct inaccurate data</li>
          <li><strong>Deletion (Right to be Forgotten):</strong> Request deletion of your data (subject to legal retention requirements)</li>
          <li><strong>Restrict Processing:</strong> Request we limit how we use your data</li>
          <li><strong>Portability:</strong> Receive your data in machine-readable format</li>
          <li><strong>Object:</strong> Object to data processing for marketing or legitimate interests</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent for optional processing at any time</li>
          <li><strong>Lodge a Complaint:</strong> File a complaint with your regional data protection authority</li>
        </ul>
        <p style={styles.p}>
          <strong>For California residents (CCPA):</strong> You have the right to:
        </p>
        <ul style={styles.list}>
          <li>Know what personal information is collected and used</li>
          <li>Delete personal information collected from you</li>
          <li>Opt-out of the sale or sharing of your personal information</li>
          <li>Correct inaccurate personal information</li>
        </ul>
        <p style={styles.p}>
          <strong>To exercise these rights, contact us at:</strong> privacy@media-magic.umangshah.me. Please allow 30 days for processing. We may require identity verification before fulfilling requests.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>9. Children's Privacy (COPPA)</h2>
        <p style={styles.p}>
          Our Service is not intended for users under 13 years old (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect information from children under 13. If we become aware a child under 13 has created an account, we will promptly delete that account and associated data.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>10. Third-Party Links & Features</h2>
        <p style={styles.p}>
          Our Service may contain links to third-party websites, services, and integrations (GitHub, documentation sites, analytics providers). We are not responsible for their privacy practices. We recommend reviewing their privacy policies independently. Our Privacy Policy does not apply to third-party services.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>11. Data Processing Agreement (DPA)</h2>
        <p style={styles.p}>
          If you use Media Magic to process personal data (e.g., images containing identifying information about people), a Data Processing Agreement may be required. For businesses handling EU user data, contact us to execute a DPA compliant with GDPR Article 28.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>12. Policy Updates & Notification</h2>
        <p style={styles.p}>
          We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or other factors. We will:
        </p>
        <ul style={styles.list}>
          <li>Post updated policies on this page with a new "Last Updated" date</li>
          <li>Notify you of material changes via email if you have an account</li>
          <li>Provide reasonable notice (at least 30 days) before changes take effect</li>
        </ul>
        <p style={styles.p}>
          Continued use of the Service after updates indicates your acceptance of changes.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>13. Contact Us</h2>
        <p style={styles.p}>
          If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, contact us at:
        </p>
        <p style={styles.p}>
          <strong>Email:</strong> privacy@media-magic.umangshah.me<br />
          <strong>Company:</strong> Media Magic<br />
          <strong>Response Time:</strong> We aim to respond to all inquiries within 10 business days
        </p>
        <p style={styles.p}>
          <strong>EU/UK Data Protection Authority Contact:</strong> If you believe we've violated your privacy rights, you may lodge a complaint with your regional supervisory authority.
        </p>
      </section>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: '1px solid var(--line-soft)',
  },
  h2: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
    color: 'var(--ink-950)',
  },
  h3: {
    fontSize: 16,
    fontWeight: 600,
    marginTop: 16,
    marginBottom: 8,
    color: 'var(--ink-950)',
  },
  p: {
    color: 'var(--ink-600)',
    lineHeight: 1.7,
    marginBottom: 12,
  },
  list: {
    marginLeft: 24,
    marginBottom: 12,
    color: 'var(--ink-600)',
    lineHeight: 1.7,
  },
};

export default PrivacyPolicy;
